import {announce} from '@github-ui/aria-live'
import {WorkflowRunDeleteWarningResponseSchema} from '@github-ui/dotcom-schema/types/actions/v1/workflow_run_delete_warning'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {useQuery} from '@github-ui/react-query'
import {Link as RouterLink} from '@github-ui/react-router'
import {GitPullRequestClosedIcon, GitPullRequestIcon} from '@primer/octicons-react'
import {Banner, Dialog, Link, Spinner} from '@primer/react'
import {useEffect, useRef} from 'react'

import styles from './WorkflowRunDeleteDialog.module.css'

interface WorkflowRunDeleteDialogProps {
  deleteUrl: string
  warningUrl?: string
  deleting: boolean
  errorMessage?: string
  onCancel: () => void
  onConfirm: (deleteUrl: string) => void
}

export function WorkflowRunDeleteDialog({
  deleteUrl,
  warningUrl,
  deleting,
  errorMessage,
  onCancel,
  onConfirm,
}: WorkflowRunDeleteDialogProps) {
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister -- Warning responses use the in-memory query cache, not persistent storage.
  const warningQuery = useQuery({
    queryKey: ['workflow-run-delete-warning-json', warningUrl],
    enabled: Boolean(warningUrl),
    queryFn: async ({signal}) => {
      const warning = await queryFnFetch(
        {queryDeps: {pathname: warningUrl ?? '', init: {signal}}},
        {schema: WorkflowRunDeleteWarningResponseSchema},
      )
      if (warning?.$typeName !== WorkflowRunDeleteWarningResponseSchema.typeName) {
        throw new Error('Invalid workflow run delete warning response')
      }
      return warning
    },
  })
  const warningPending = Boolean(warningUrl) && warningQuery.isPending
  const pullRequests = warningQuery.data?.pullRequests ?? []
  const pullRequestCount = pullRequests.length
  const pullRequestLabel = pullRequestCount === 1 ? 'pull request' : 'pull requests'
  const dialogRef = useRef<HTMLDivElement>(null)
  const wasWarningPendingRef = useRef(false)

  useEffect(() => {
    if (warningPending) {
      announce('Loading workflow run delete warning')
    } else if (wasWarningPendingRef.current && pullRequestCount > 0) {
      announce(`${pullRequestCount} ${pullRequestLabel} may be affected.`, {element: dialogRef.current ?? undefined})
    }
    wasWarningPendingRef.current = warningPending
  }, [warningPending, pullRequestCount, pullRequestLabel])

  useEffect(() => {
    if (!errorMessage) return

    announce(errorMessage, {assertive: true})
    const animationFrame = requestAnimationFrame(() => {
      dialogRef.current
        ?.querySelector<HTMLButtonElement>('[data-component="Dialog.FooterButton"][data-variant="danger"]')
        ?.focus()
    })
    return () => cancelAnimationFrame(animationFrame)
  }, [errorMessage])

  return (
    <Dialog
      ref={dialogRef}
      title="Delete workflow run"
      role="alertdialog"
      footerButtons={[
        {
          content: 'Cancel',
          autoFocus: true,
          onClick: onCancel,
        },
        {
          content: 'Yes, delete this workflow run',
          buttonType: 'danger',
          disabled: warningPending,
          loading: deleting,
          onClick: () => {
            if (!deleting) onConfirm(deleteUrl)
          },
        },
      ]}
      onClose={onCancel}
      width="large"
    >
      <p>
        Are you sure you want to permanently delete this workflow run? <strong>This action cannot be undone.</strong>
      </p>
      {warningPending && (
        <div className={styles.Loading}>
          <Spinner size="medium" srText="Loading workflow run delete warning" />
        </div>
      )}
      {pullRequestCount > 0 && (
        <>
          <p>{`The following ${pullRequestLabel} may not be able to be merged if this run is deleted:`}</p>
          <ul className={styles.PullRequests}>
            {pullRequests.map(pullRequest => (
              <li key={pullRequest.number} className={styles.PullRequest}>
                <span className={pullRequest.isOpen ? 'color-fg-success' : 'color-fg-danger'}>
                  {pullRequest.isOpen ? <GitPullRequestIcon /> : <GitPullRequestClosedIcon />}
                  <span className="sr-only">
                    {pullRequest.isOpen ? 'Open pull request: ' : 'Closed pull request: '}
                  </span>
                </span>
                <span className={styles.PullRequestText}>
                  <Link as={RouterLink} inline to={pullRequest.url}>
                    {pullRequest.title}
                  </Link>{' '}
                  <span className="color-fg-muted">#{pullRequest.number}</span>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
      {errorMessage && <Banner title={errorMessage} variant="critical" flush />}
    </Dialog>
  )
}
